// src/components/layout/HriFooter.jsx
import React from "react";
import {
    Box,
    Grid,
    Typography,
    Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import packageJson from "../../../../package.json";
import { useI18n } from "../../../i18n/I18nContext";

const fallbackIntro = {
    de: "<p>Das umfangreichste Hotel- und Gastronomieportal der Schweiz für Urlaub, Tourismus und Business.</p>",
    en: "<p>The most comprehensive hotel and gastronomy portal in Switzerland for holidays, tourism and business.</p>",
    fr: "<p>Le portail hôtelier et gastronomique le plus complet de Suisse pour les vacances, le tourisme et les affaires.</p>",
    it: "<p>Il portale alberghiero e gastronomico più completo della Svizzera per vacanze, turismo e business.</p>",
};

const fallbackContact = {
    de: "<p><strong>Tourismus-Schweiz.ch GmbH</strong><br>Hüttenweg 16<br>CH-8254 Basadingen</p><p>Telefon: +41 (0)52 654 18 10<br>E-Mail: mail@tourismus-schweiz.ch</p>",
    en: "<p><strong>Tourismus-Schweiz.ch GmbH</strong><br>Hüttenweg 16<br>CH-8254 Basadingen</p><p>Phone: +41 (0)52 654 18 10<br>Email: mail@tourismus-schweiz.ch</p>",
    fr: "<p><strong>Tourismus-Schweiz.ch GmbH</strong><br>Hüttenweg 16<br>CH-8254 Basadingen</p><p>Téléphone: +41 (0)52 654 18 10<br>E-mail: mail@tourismus-schweiz.ch</p>",
    it: "<p><strong>Tourismus-Schweiz.ch GmbH</strong><br>Hüttenweg 16<br>CH-8254 Basadingen</p><p>Telefono: +41 (0)52 654 18 10<br>E-mail: mail@tourismus-schweiz.ch</p>",
};

const fallbackInfoLinks = {
    de: "Meteo Schweiz | #\nWährungsrechner | #\nSBB CFF FFS | #\nSchifffahrt Schweiz | #",
    en: "Swiss weather | #\nCurrency converter | #\nSBB CFF FFS | #\nSwiss boat services | #",
    fr: "Météo Suisse | #\nConvertisseur de devises | #\nSBB CFF FFS | #\nNavigation Suisse | #",
    it: "Meteo Svizzera | #\nConvertitore di valuta | #\nSBB CFF FFS | #\nNavigazione Svizzera | #",
};

const fallbackTourismLinks = {
    de: "Veranstaltungen Schweiz | #\nNeueste Mitglieder | /neueste-mitglieder\nDatenschutzgesetz | #",
    en: "Events Switzerland | #\nLatest members | /neueste-mitglieder\nData protection law | #",
    fr: "Événements Suisse | #\nNouveaux membres | /neueste-mitglieder\nLoi sur la protection des données | #",
    it: "Eventi Svizzera | #\nNuovi membri | /neueste-mitglieder\nLegge sulla protezione dei dati | #",
};

function localizedHtml(config, field, lang, fallback) {
    return config?.[`${field}_${lang}`] || config?.[`${field}_de`] || fallback[lang] || fallback.de;
}

function localizedText(config, field, lang, fallback) {
    return config?.[`${field}_${lang}`] || config?.[`${field}_de`] || fallback[lang] || fallback.de || "";
}

function parseFooterLinks(value) {
    const raw = String(value || "").trim();
    if (!raw) return [];

    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed
                .map((item) => ({
                    label: String(item?.label || "").trim(),
                    href: String(item?.url || item?.href || "#").trim() || "#",
                    new_window: !!item?.new_window,
                }))
                .filter((item) => item.label);
        }
    } catch {
        // keep support for legacy line based seed values
    }

    return raw
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const [label, ...rest] = line.split("|");
            return {
                label: (label || "").trim(),
                href: (rest.join("|") || "#").trim() || "#",
                new_window: false,
            };
        })
        .filter((item) => item.label);
}

function FooterLink({ href, children, onClick, newWindow = false }) {
    const isButtonLike = typeof onClick === "function";
    return (
        <Link
            component={isButtonLike ? "button" : "a"}
            href={isButtonLike ? undefined : href}
            onClick={onClick}
            target={!isButtonLike && newWindow ? "_blank" : undefined}
            rel={!isButtonLike && newWindow ? "noopener noreferrer" : undefined}
            color="inherit"
            underline="none"
            sx={{
                display: "block",
                width: "fit-content",
                p: 0,
                border: 0,
                bgcolor: "transparent",
                cursor: "pointer",
                fontSize: "0.88rem",
                lineHeight: 1.9,
                color: "rgba(255,255,255,0.74)",
                textAlign: "left",
                "&:hover": { color: "var(--domain-primary)" },
            }}
        >
            {children}
        </Link>
    );
}

function FooterColumn({ title, children }) {
    return (
        <Box>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "0.98rem", mb: 1.2 }}>
                {title}
            </Typography>
            {children}
        </Box>
    );
}

export default function HriFooter({ mt = 4, domainConfig = {} }) {
    const navigate = useNavigate();
    const { lang, t } = useI18n();
    const introHtml = localizedHtml(domainConfig, "footer_intro", lang, fallbackIntro);
    const contactHtml = localizedHtml(domainConfig, "footer_contact", lang, fallbackContact);
    const infoLinks = parseFooterLinks(localizedText(domainConfig, "footer_info_links", lang, fallbackInfoLinks));
    const tourismLinks = parseFooterLinks(localizedText(domainConfig, "footer_tourism_links", lang, fallbackTourismLinks));

    return (
        <Box
            component="footer"
            sx={{
                mt,
                bgcolor: "var(--domain-footer-background)",
                color: "var(--domain-footer-text)",
                borderTop: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
        >
            <Box sx={{ maxWidth: 1540, mx: "auto", px: { xs: 2, md: 4 }, pt: { xs: 4, md: 4.5 }, pb: 3 }}>
                <Grid container spacing={{ xs: 3.5, md: 5 }}>
                    <Grid item xs={12} md={3}>
                        <Box
                            component="img"
                            src={domainConfig.footer_logo ? (/^https?:\/\//i.test(domainConfig.footer_logo) ? domainConfig.footer_logo : `${process.env.REACT_APP_BACKEND_URL || ""}${domainConfig.footer_logo}`) : `${process.env.PUBLIC_URL}/images/HRI_Logo_rz_RGB.png`}
                            alt="hotel restaurant info"
                            sx={{ width: 128, maxWidth: "100%", mb: 1.7 }}
                            onError={(e) => {
                                e.currentTarget.src = `${process.env.PUBLIC_URL}/images/favicon.png`;
                            }}
                        />
                        <Box
                            sx={{
                                color: "rgba(255,255,255,0.78)",
                                fontSize: "0.88rem",
                                lineHeight: 1.65,
                                maxWidth: 300,
                                "& p": { m: 0, mb: 1 },
                                "& strong": { color: "#fff" },
                            }}
                            dangerouslySetInnerHTML={{ __html: introHtml }}
                        />
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <FooterColumn title={t("footer.infoNews")}>
                            {infoLinks.map((item, index) => (
                                <FooterLink key={`${item.label}-${index}`} href={item.href} newWindow={item.new_window}>{item.label}</FooterLink>
                            ))}
                        </FooterColumn>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <FooterColumn title={t("footer.tourismOffers")}>
                            {tourismLinks.map((item, index) => (
                                <FooterLink
                                    key={`${item.label}-${index}`}
                                    href={item.href}
                                    newWindow={item.new_window}
                                    onClick={!item.new_window && item.href.startsWith("/") ? () => navigate(item.href) : undefined}
                                >
                                    {item.label}
                                </FooterLink>
                            ))}
                        </FooterColumn>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <FooterColumn title={t("footer.guests")}>
                            <FooterLink href="#">{t("footer.reportRestaurant")}</FooterLink>
                        </FooterColumn>
                        <Box sx={{ mt: 2.5 }}>
                            <FooterColumn title={t("footer.operators")}>
                                <FooterLink href="#">{t("footer.joinRegister")}</FooterLink>
                                <FooterLink href="#">{t("footer.newHomepage")}</FooterLink>
                            </FooterColumn>
                        </Box>
                        <Box sx={{ mt: 2.5 }}>
                            <FooterColumn title="Speiserestaurant.ch">
                                <FooterLink onClick={() => navigate("/partner")}>{t("footer.ourPartners")}</FooterLink>
                                <FooterLink onClick={() => navigate("/admin")}>{t("footer.internalArea")}</FooterLink>
                            </FooterColumn>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FooterColumn title={t("nav.contact")}>
                            <Box
                                sx={{
                                    color: "rgba(255,255,255,0.76)",
                                    fontSize: "0.88rem",
                                    lineHeight: 1.7,
                                    "& p": { m: 0, mb: 1.25 },
                                    "& strong": { color: "#fff" },
                                    "& a": { color: "inherit" },
                                }}
                                dangerouslySetInnerHTML={{ __html: contactHtml }}
                            />
                        </FooterColumn>
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        mt: 3.5,
                        pt: 2.2,
                        borderTop: "1px solid rgba(255,255,255,0.08)",
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "flex-start", sm: "center" },
                        justifyContent: "space-between",
                        gap: 1.5,
                        color: "rgba(255,255,255,0.68)",
                    }}
                >
                    <Typography sx={{ fontSize: "0.82rem" }}>
                        © Copyright 2026 by Tourismusinfo GmbH. All Rights Reserved. - Software Version: {packageJson.version}
                    </Typography>
                    <Box sx={{ display: "flex", gap: { xs: 2, sm: 3 } }}>
                        <FooterLink onClick={() => navigate("/kontakt")}>{t("nav.contact")}</FooterLink>
                        <FooterLink onClick={() => navigate("/impressum")}>{t("footer.imprint")}</FooterLink>
                        <FooterLink href="#">{t("footer.privacy")}</FooterLink>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
